import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListItem } from '../ui/ListItem.js';
import { LoadingState } from '../ui/LoadingState.js';
import { EmptyState } from '../ui/EmptyState.js';
import { Modal } from '../ui/Modal.js';
import { Input } from '../ui/Input.js';
import { Button } from '../ui/Button.js';
import { Field } from '../ui/Field.js';
import { Pill, type PillTone } from '../PillGroup.js';
import { formatMoney } from '../../lib/money.js';
import { paymentMethodLabel } from '../../lib/paymentMethodLabel.js';
import { Textarea } from '../ui/Textarea.js';
import {
  useRecurringCharges, useChangeChargeAmount, useCancelRecurringCharge,
  useComposeAmountChangeNotice, type RecurringCharge,
} from '../../hooks/useClientPayments.js';

// ── Lista de cobros recurrentes (Fase 4, 2026-08-31) ─────────────────────────
// docs/PLAN_cobro_a_clientes.md
//
// Lista única full-width. No es split-pane: un acuerdo recurrente son cinco
// datos, y abrirle un panel de detalle sería un panel vacío al lado de una
// lista corta.
//
// SIN checkboxes de selección, a diferencia del resto de los módulos de lista
// (REGLA #18) — desvío deliberado, reportado a Eduardo:
//
//   1. No hay ninguna acción masiva que tenga sentido acá. Cancelarle el cobro
//      a varios clientes de una sola vez no es algo que alguien quiera hacer, y
//      Eduardo ya decidió que cancelar es una conversación, no un botón.
//   2. Un checkbox sin acción detrás es UI muerta.
//   3. La SelectionActionBar de la página solo sabe borrar documentos. Con
//      checkboxes acá, seleccionar un acuerdo y apretar borrar habría llamado a
//      bulkDeleteInvoices con el id del ACUERDO — o sea, borrado la factura que
//      tuviera ese número.
//
// Si aparece una acción masiva real, entran los checkboxes y la barra propia.
//
// Las dos acciones viven en la fila: cambiar monto y cancelar, una por vez y
// mirando de quién se trata.

const STATUS_TONES: Record<RecurringCharge['status'], PillTone> = {
  pending_card: 'amber',
  active: 'success',
  past_due: 'danger',
  canceled: 'neutral',
};

function formatFecha(ms: number): string {
  return new Date(ms).toLocaleDateString('es', { year: 'numeric', month: 'short', day: 'numeric' });
}

export function RecurringChargesPanel() {
  const { t } = useTranslation();
  const { data, isLoading } = useRecurringCharges();
  const changeAmount = useChangeChargeAmount();
  const cancelCharge = useCancelRecurringCharge();

  const composeNotice = useComposeAmountChangeNotice();

  const [editing, setEditing] = useState<RecurringCharge | null>(null);
  const [amountDollars, setAmountDollars] = useState('');
  // Dos pasos en el mismo modal, no uno solo: cambiar el precio en Stripe y
  // avisarle al cliente son acciones separadas (decisión de Eduardo), y el
  // segundo paso puede saltearse. Encadenarlas mandaría un aviso sin que nadie
  // haya decidido mandarlo.
  const [paso, setPaso] = useState<'monto' | 'aviso'>('monto');
  const [motivo, setMotivo] = useState('');
  const [avisoListo, setAvisoListo] = useState(false);

  const charges = data?.charges ?? [];

  function openAmountModal(charge: RecurringCharge) {
    setEditing(charge);
    setAmountDollars((charge.amountCents / 100).toFixed(2));
    setPaso('monto');
    setMotivo('');
    setAvisoListo(false);
  }

  function cerrarModal() {
    setEditing(null);
  }

  function submitAmount() {
    if (!editing) return;
    const cents = Math.round((Number(amountDollars) || 0) * 100);
    if (cents <= 0 || cents === editing.amountCents) return;
    changeAmount.mutate({ id: editing.id, amountCents: cents }, { onSuccess: () => setPaso('aviso') });
  }

  function prepararAviso() {
    if (!editing) return;
    composeNotice.mutate(
      { id: editing.id, ...(motivo.trim() ? { reason: motivo.trim() } : {}) },
      { onSuccess: () => setAvisoListo(true) },
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ flex: 1, overflowY: 'auto' }}>
        {isLoading && <div style={{ padding: 'var(--evo-space-4)' }}><LoadingState size="sm" /></div>}

        {!isLoading && charges.length === 0 && (
          <EmptyState
            icon="billing"
            title={t('billing.recurring.emptyTitle')}
            description={t('billing.recurring.emptyDescription')}
          />
        )}

        {!isLoading && charges.map(charge => (
          <ListItem
            key={charge.id}
            itemId={charge.id}
            title={charge.contactName ?? t('billing.facturacion.unknownClient')}
            subtitle={charge.description}
            meta={[
              `${formatMoney(charge.amountCents, charge.currency)} · ${t(charge.interval === 'year' ? 'billing.payment.perYear' : 'billing.payment.perMonth')}`,
              // Solo hay próximo cobro si el acuerdo está vivo y con tarjeta.
              charge.currentPeriodEnd
                ? `${t('billing.payment.nextCharge')}: ${formatFecha(charge.currentPeriodEnd)}`
                : null,
              paymentMethodLabel(charge.cardBrand, charge.cardLast4) ?? null,
            ].filter(Boolean).join(' · ')}
            badges={[
              <Pill key="status" tone={STATUS_TONES[charge.status]} size="xs">
                {t(`billing.payment.charge_${charge.status}`)}
              </Pill>,
              ...(charge.cardStatus === 'failing'
                ? [<Pill key="card" tone="danger" size="xs">{t('billing.payment.cardFailing')}</Pill>]
                : []),
            ]}
            actions={charge.status !== 'canceled' ? (
              <>
                <Button variant="outline" tone="cyan" size="sm" onClick={() => openAmountModal(charge)}>
                  {t('billing.recurring.changeAmount')}
                </Button>
                <Button
                  variant="outline"
                  tone="danger"
                  size="sm"
                  loading={cancelCharge.isPending}
                  onClick={() => cancelCharge.mutate({ id: charge.id })}
                >
                  {t('billing.recurring.cancel')}
                </Button>
              </>
            ) : undefined}
            density="default"
          />
        ))}
      </div>

      {editing && (
        <Modal
          open
          onClose={cerrarModal}
          title={t('billing.recurring.changeAmountTitle')}
        >
          {paso === 'monto' ? (
            <>
              <Field
                label={t('billing.recurring.newAmount')}
                hint={t('billing.recurring.changeAmountHint')}
              >
                <Input type="number" value={amountDollars} onChange={setAmountDollars} size="sm" fullWidth />
              </Field>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--evo-space-2)', marginTop: 'var(--evo-space-4)' }}>
                <Button variant="ghost" tone="muted" onClick={cerrarModal}>
                  {t('common.cancel')}
                </Button>
                <Button variant="solid" tone="cyan" loading={changeAmount.isPending} onClick={submitAmount}>
                  {t('billing.recurring.confirmChange')}
                </Button>
              </div>
            </>
          ) : avisoListo ? (
            // El borrador quedó esperando en la factura de origen, no salió
            // solo. Decir DÓNDE está evita que alguien lo dé por enviado.
            <>
              <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', margin: 0, lineHeight: 1.6 }}>
                {t('billing.recurring.noticeReady')}
              </p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'var(--evo-space-4)' }}>
                <Button variant="solid" tone="cyan" onClick={cerrarModal}>
                  {t('common.close')}
                </Button>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-2)', margin: '0 0 var(--evo-space-4)', lineHeight: 1.6 }}>
                {t('billing.recurring.amountSaved')}
              </p>
              <Field
                label={t('billing.recurring.noticeReason')}
                hint={t('billing.recurring.noticeReasonHint')}
              >
                <Textarea value={motivo} onChange={setMotivo} rows={3} fullWidth />
              </Field>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--evo-space-2)', marginTop: 'var(--evo-space-4)' }}>
                <Button variant="ghost" tone="muted" onClick={cerrarModal}>
                  {t('billing.recurring.noticeSkip')}
                </Button>
                <Button variant="solid" tone="cyan" loading={composeNotice.isPending} onClick={prepararAviso}>
                  {t('billing.recurring.noticePrepare')}
                </Button>
              </div>
            </>
          )}
        </Modal>
      )}

    </div>
  );
}
